import { useState, useEffect, useRef } from 'react';
import { useTranslation } from '../../context/LanguageContext';
import {
  X, Send, Loader2, Clock, CheckCircle, AlertCircle,
  MessageSquare, Lock, User
} from 'lucide-react';
import { getTicketById, addMessage, closeTicket, STATUS_COLORS, PRIORITY_COLORS } from '../../services/supportService';
import { getStoredUser as getUser } from '../../context/AuthContext';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import ConfirmModal from '../ui/ConfirmModal';

const TicketDetail = ({ ticket, onClose, onUpdated }) => {
  const { t } = useTranslation();
  const user = getUser();
  const messagesEndRef = useRef(null);

  const [ticketData, setTicketData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const [closing, setClosing] = useState(false);

  // Load ticket details
  const loadTicket = async () => {
    if (!ticket?.id || !user?.id) return;

    setLoading(true);
    try {
      const result = await getTicketById(ticket.id, user.id);
      if (result.success) {
        setTicketData(result.data);
      }
    } catch (error) {
      console.error('Error loading ticket:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTicket();
  }, [ticket?.id, user?.id]);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [ticketData?.messages]);

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get status icon
  const getStatusIcon = (status) => {
    switch (status) {
      case 'open':
        return <AlertCircle className="w-4 h-4" />;
      case 'in_progress':
        return <Clock className="w-4 h-4" />;
      case 'waiting':
        return <Clock className="w-4 h-4" />;
      case 'resolved':
        return <CheckCircle className="w-4 h-4" />;
      case 'closed':
        return <Lock className="w-4 h-4" />;
      default:
        return null;
    }
  };

  // Send message
  const handleSendMessage = async (e) => {
    e.preventDefault();

    if (!newMessage.trim() || sending) return;

    setSending(true);
    try {
      const result = await addMessage(ticket.id, user.id, newMessage.trim());
      if (result.success) {
        setNewMessage('');
        await loadTicket();
        onUpdated();
      }
    } catch (error) {
      console.error('Error sending message:', error);
    } finally {
      setSending(false);
    }
  };

  // Close ticket
  const handleCloseTicket = async () => {
    setClosing(true);
    try {
      const result = await closeTicket(ticket.id, user.id);
      if (result.success) {
        setShowCloseConfirm(false);
        await loadTicket();
        onUpdated();
      }
    } catch (error) {
      console.error('Error closing ticket:', error);
    } finally {
      setClosing(false);
    }
  };

  const statusColor = ticketData ? STATUS_COLORS[ticketData.status] || STATUS_COLORS.open : STATUS_COLORS.open;
  const priorityColor = ticketData ? PRIORITY_COLORS[ticketData.priority] || PRIORITY_COLORS.normal : PRIORITY_COLORS.normal;
  const isClosed = ticketData?.status === 'closed';

  return (
    <Modal onClose={onClose} title={`#${ticket.id} - ${ticket.subject}`} maxWidth="max-w-3xl">
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
        </div>
      ) : ticketData ? (
        <div className="flex flex-col h-[70vh]">
          {/* Header info */}
          <div className="flex flex-wrap items-center gap-3 pb-4 border-b border-gray-200 dark:border-gray-700">
            {/* Status badge */}
            <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium ${statusColor.bg} ${statusColor.text}`}>
              {getStatusIcon(ticketData.status)}
              {t.support?.status?.[ticketData.status] || ticketData.status}
            </span>

            {/* Priority badge */}
            <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium ${priorityColor.bg} ${priorityColor.text}`}>
              {t.support?.priority?.[ticketData.priority] || ticketData.priority}
            </span>

            {/* Category */}
            <span className="text-sm text-gray-500 dark:text-gray-400">
              {t.support?.category?.[ticketData.category] || ticketData.category}
            </span>

            {/* Date */}
            <span className="text-sm text-gray-400 ml-auto">
              {formatDate(ticketData.createdat)}
            </span>
          </div>

          {/* Original description */}
          <div className="py-4 border-b border-gray-200 dark:border-gray-700">
            <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
              {ticketData.description}
            </p>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto py-4 space-y-4">
            {ticketData.messages && ticketData.messages.length > 0 ? (
              ticketData.messages.map((msg) => {
                const isAdmin = msg.isadminreply === true || msg.isadminreply === 1;
                const isMe = msg.peopleid === user.id;

                return (
                  <div
                    key={msg.id}
                    className={`flex ${isMe && !isAdmin ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[80%] rounded-lg p-3 ${
                        isAdmin
                          ? 'bg-purple-100 dark:bg-purple-900/30 border border-purple-200 dark:border-purple-800'
                          : isMe
                          ? 'bg-blue-100 dark:bg-blue-900/30'
                          : 'bg-gray-100 dark:bg-gray-700'
                      }`}
                    >
                      {/* Sender info */}
                      <div className="flex items-center gap-2 mb-1">
                        {isAdmin ? (
                          <span className="text-xs font-medium text-purple-700 dark:text-purple-400">
                            {t.support?.adminReply || 'Réponse admin'}
                          </span>
                        ) : (
                          <span className="text-xs font-medium text-gray-600 dark:text-gray-400">
                            {msg.firstname} {msg.lastname}
                          </span>
                        )}
                        <span className="text-xs text-gray-400">
                          {formatDate(msg.createdat)}
                        </span>
                      </div>

                      {/* Message content */}
                      <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                        {msg.message}
                      </p>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="text-center text-gray-500 dark:text-gray-400 py-8">
                <MessageSquare className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p>{t.support?.noMessages || 'Aucun message'}</p>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input area */}
          {!isClosed ? (
            <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
              <form onSubmit={handleSendMessage} className="flex gap-3">
                <textarea
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder={t.support?.messagePlaceholder || 'Écrivez votre message...'}
                  rows={2}
                  className="flex-1 px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 focus:ring-2 focus:ring-purple-500 resize-none"
                />
                <div className="flex flex-col gap-2">
                  <Button
                    type="submit"
                    disabled={!newMessage.trim() || sending}
                  >
                    {sending ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4" />
                    )}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowCloseConfirm(true)}
                    className="text-xs"
                  >
                    {t.support?.closeTicket || 'Fermer'}
                  </Button>
                </div>
              </form>
            </div>
          ) : (
            <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-center gap-2 text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 rounded-lg p-3">
                <Lock className="w-4 h-4" />
                <span>{t.support?.ticketClosed || 'Ce ticket est fermé'}</span>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="text-center py-12 text-gray-500">
          {t.support?.ticketNotFound || 'Ticket non trouvé'}
        </div>
      )}

      {/* Close confirmation modal */}
      {showCloseConfirm && (
        <ConfirmModal
          title={t.support?.closeTicketTitle || 'Fermer le ticket'}
          message={t.support?.closeTicketConfirm || 'Êtes-vous sûr de vouloir fermer ce ticket ?'}
          confirmText={t.support?.closeTicket || 'Fermer'}
          cancelText={t.common?.cancel || 'Annuler'}
          onConfirm={handleCloseTicket}
          onCancel={() => setShowCloseConfirm(false)}
          loading={closing}
        />
      )}
    </Modal>
  );
};

export default TicketDetail;
